import { createFileRoute, Link } from "@tanstack/react-router";

import { useDashboardStats, useOrganisations } from "../api/hooks";

export const Route = createFileRoute("/")({
  component: Dashboard,
});

function Dashboard() {
  return (
    <>
      <div className="page-header">
        <div className="page-title">Dashboard</div>
        <div className="page-subtitle">
          Ecosystem Tracker overview
        </div>
      </div>

      <AlertBanner />
      <StatsRow />
      <RevenueRow />

      <div className="grid-2 wide-left">
        <RecentOrganisations />
        <ActivityFeed />
      </div>

      <div className="grid-2">
        <ReferralPanel />
        <StoragePanel />
      </div>
    </>
  );
}

function AlertBanner() {
  return (
    <div className="alert-banner">
      <div className="alert-icon">&#x26A0;&#xFE0F;</div>
      <div className="alert-text">
        <strong>2 referrals pending verification</strong> - waiting for account
        verification before storage bonuses are applied.
      </div>
      <Link to="/referrals" className="alert-action">
        Review
      </Link>
    </div>
  );
}

function StatsRow() {
  const { data, isLoading, isError } = useDashboardStats();

  const showValue = !isLoading && !isError;

  return (
    <div className="stats-row">
      <div className="stat-card">
        <div className="stat-label">Organisations</div>
        <div className="stat-value">
          {showValue ? data?.organisationCount : "-"}
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-label">Total Users</div>
        <div className="stat-value">
          {showValue ? data?.userCount : "-"}
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-label">Active Sites</div>
        <div className="stat-value">
          {showValue ? data?.siteCount : "-"}
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-label">SpeciesNet Classifications</div>
        <div className="stat-value">-</div>
        <div className="stat-sub">Coming soon</div>
      </div>
    </div>
  );
}

function RevenueRow() {
  return (
    <div className="rev-row">
      <div className="rev-card">
        <div className="rev-label">MRR</div>
        <div className="rev-value">-</div>
        <div className="rev-sub">Coming soon</div>
      </div>
      <div className="rev-card">
        <div className="rev-label">ARR</div>
        <div className="rev-value">-</div>
        <div className="rev-sub">Coming soon</div>
      </div>
      <div className="rev-card">
        <div className="rev-label">Storage Revenue</div>
        <div className="rev-value">-</div>
        <div className="rev-sub">Coming soon</div>
      </div>
    </div>
  );
}

function StorageBar({
  percent,
  variant,
}: {
  percent: number;
  variant?: "high" | "critical";
}) {
  return (
    <div className="storage-bar">
      <div
        className={`storage-bar-fill ${variant ?? ""}`}
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

function RecentOrganisations() {
  const { data, isLoading, isError } = useOrganisations();

  return (
    <div className="panel">
      <div className="panel-title">
        Recent organisations
        <Link to="/organisations" className="panel-link">
          View all
        </Link>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>Organisation</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={2}>Loading...</td>
            </tr>
          ) : isError ? (
            <tr>
              <td colSpan={2}>Failed to load organisations</td>
            </tr>
          ) : (
            data?.items.map((org) => (
              <tr key={org.id}>
                <td className="tbl-org">{org.name}</td>
                <td>
                  {new Date(org.created).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </td>
              </tr>
            ))
          )}
          {!isLoading && !isError && data?.items.length === 0 && (
            <tr>
              <td colSpan={2}>No organisations yet</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ActivityFeed() {
  const activities = [
    {
      type: "signup",
      text: (
        <>
          <strong>Sussex Flow Initiative</strong> signed up (Starter)
        </>
      ),
      time: "2 hours ago",
    },
    {
      type: "referral",
      text: (
        <>
          <strong>Heal Rewilding</strong> referred by{" "}
          <strong>Knepp Wildland</strong> - pending verification
        </>
      ),
      time: "Yesterday",
    },
    {
      type: "upgrade",
      text: (
        <>
          <strong>Wild Ken Hill</strong> added a 100 GB storage add-on
        </>
      ),
      time: "2 days ago",
    },
    {
      type: "storage",
      text: (
        <>
          <strong>Wild Ken Hill</strong> approaching storage limit (89%)
        </>
      ),
      time: "2 days ago",
    },
    {
      type: "upgrade",
      text: (
        <>
          <strong>Cambrian Wildwood</strong> upgraded to Pro, reverted to
          Starter
        </>
      ),
      time: "4 days ago",
    },
    {
      type: "signup",
      text: (
        <>
          <strong>Northwoods Rewilding</strong> signed up (Starter)
        </>
      ),
      time: "5 days ago",
    },
    {
      type: "referral",
      text: (
        <>
          Referral bonus applied: <strong>Knepp Wildland</strong> +10 GB,{" "}
          <strong>Wild Ken Hill</strong> +10 GB
        </>
      ),
      time: "1 week ago",
    },
  ];

  return (
    <div className="panel">
      <div className="panel-title">Recent activity</div>
      {activities.map((activity, i) => (
        <div key={i} className="activity-item">
          <div className={`activity-dot ${activity.type}`} />
          <div>
            <div className="activity-text">{activity.text}</div>
            <div className="activity-time">{activity.time}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReferralPanel() {
  const referrals = [
    {
      referrer: "Knepp Wildland",
      referred: "Heal Rewilding",
      status: "Pending",
    },
    {
      referrer: "Knepp Wildland",
      referred: "Wild Ken Hill",
      status: "Verified",
    },
    {
      referrer: "Wild Ken Hill",
      referred: "Northwoods Rewilding",
      status: "Pending",
    },
  ];

  return (
    <div className="panel">
      <div className="panel-title">
        Referral programme
        <Link to="/referrals" className="panel-link">
          Manage
        </Link>
      </div>
      <div className="ref-stat-row">
        <div className="ref-stat">
          <div className="ref-stat-val">7</div>
          <div className="ref-stat-label">Total referrals</div>
        </div>
        <div className="ref-stat">
          <div className="ref-stat-val">2</div>
          <div className="ref-stat-label">Pending verification</div>
        </div>
        <div className="ref-stat">
          <div className="ref-stat-val">42 GB</div>
          <div className="ref-stat-label">Total storage given away</div>
        </div>
        <div className="ref-stat">
          <div className="ref-stat-val">£0.50</div>
          <div className="ref-stat-label">Monthly R2 cost of referrals</div>
        </div>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>Referrer</th>
            <th>Referred</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {referrals.map((r, i) => (
            <tr key={i}>
              <td className="tbl-org">{r.referrer}</td>
              <td>{r.referred}</td>
              <td>
                <span
                  className={`badge ${r.status === "Verified" ? "badge-verified" : "badge-pending"}`}
                >
                  {r.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StoragePanel() {
  return (
    <div className="panel">
      <div className="panel-title">
        Storage overview
        <Link to="/storage" className="panel-link">
          Details
        </Link>
      </div>
      <div className="ref-stat-row">
        <div className="ref-stat">
          <div className="ref-stat-val">1.2 TB</div>
          <div className="ref-stat-label">Total allocated</div>
        </div>
        <div className="ref-stat">
          <div className="ref-stat-val">358 GB</div>
          <div className="ref-stat-label">Currently used</div>
        </div>
        <div className="ref-stat">
          <div className="ref-stat-val">29%</div>
          <div className="ref-stat-label">Utilisation rate</div>
        </div>
        <div className="ref-stat">
          <div className="ref-stat-val">£4.30</div>
          <div className="ref-stat-label">Monthly R2 cost</div>
        </div>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--g700)", marginBottom: 8 }}>
        Orgs approaching limits
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>Organisation</th>
            <th className="tbl-right">Usage</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="tbl-org">Wild Ken Hill</td>
            <td className="tbl-right">
              <span style={{ fontSize: 11, fontWeight: 600, color: "var(--cta)" }}>
                89%
              </span>
              <StorageBar percent={89} variant="high" />
            </td>
          </tr>
          <tr>
            <td className="tbl-org">Cambrian Wildwood</td>
            <td className="tbl-right">
              <span style={{ fontSize: 11, fontWeight: 600 }}>62%</span>
              <StorageBar percent={62} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
